/**
 * Customer Portal shell.
 *
 * Deliberately NOT ModernMainLayout. That layout is built around the ERP's
 * permission-driven sidebar (`usePermissions`), the tenant switcher and the
 * staff notification tray — none of which a customer has, and all of which would
 * either crash or leak the shape of the internal application. This shell shows
 * only what a portal role unlocks, driven by the `capabilities` list the server
 * sends, so the client never keeps a second copy of the role map.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar, Avatar, Badge, Box, Button, CircularProgress, Container, CssBaseline,
  Divider, Drawer, GlobalStyles, IconButton, List, ListItemButton, ListItemIcon,
  ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography, alpha,
  useMediaQuery,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import customerPortalService, {
  clearPortalSession, getStoredProfile, hasPortalSession,
  portalHttp, PORTAL_API_ROOT,
} from '../../services/customerPortalService';
import useWebPush from '../../hooks/useWebPush';

const DRAWER_WIDTH = 248;

const PortalContext = createContext({ profile: null, branding: null, refresh: () => {} });

export const usePortal = () => useContext(PortalContext);

/** Capability gate for a page's own content. Mirrors the server's list. */
export const usePortalCapability = (capability) => {
  const { profile } = usePortal();
  if (!capability) return true;
  return Boolean(profile?.capabilities?.includes(capability));
};

const NAV_ITEMS = [
  { label: 'Dashboard', to: '/portal', icon: <DashboardOutlinedIcon />, exact: true },
  { label: 'Orders', to: '/portal/orders', icon: <ReceiptLongOutlinedIcon />, capability: 'view_orders' },
  { label: 'Shipments', to: '/portal/shipments', icon: <LocalShippingOutlinedIcon />, capability: 'view_shipments' },
  { label: 'Finance', to: '/portal/finance', icon: <PaymentsOutlinedIcon />, capability: 'view_finance', feature: 'finance' },
  { label: 'Documents', to: '/portal/documents', icon: <DescriptionOutlinedIcon />, capability: 'view_documents' },
  { label: 'Analytics', to: '/portal/analytics', icon: <AssessmentOutlinedIcon />, capability: 'view_analytics', feature: 'analytics' },
  // Gated on reading the thread, NOT on raising an enquiry: the `raise_ticket`
  // feature flag only turns off *starting* a new one (the API enforces that in
  // PortalTicketViewSet.create), so hiding the whole section would also bury
  // the conversations already under way.
  { label: 'Support', to: '/portal/support', icon: <SupportAgentOutlinedIcon />, capability: 'view_tickets' },
  // No `capability`: project chat is not a portal role, it is an ADMISSION. A
  // project team adds this contact to a specific project's conversation, and
  // the page shows only what they were admitted to — so gating the menu item
  // on a portal capability would hide a conversation somebody has already been
  // invited into.
  { label: 'Project Chat', to: '/portal/chat', icon: <ChatBubbleOutlineIcon /> },
];

const PortalLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:900px)');

  const [profile, setProfile] = useState(getStoredProfile());
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  // A customer contact is admitted to a project conversation by somebody
  // inside, and then has no reason to keep the portal open waiting for a
  // reply. Push is how they hear about one — on the portal's own prefix,
  // because the ERP `/api/push/` path refuses a portal token at the edge.
  useWebPush({
    client: portalHttp,
    base: `${PORTAL_API_ROOT}/push`,
    signedIn: hasPortalSession(),
  });

  const bootstrap = useCallback(async () => {
    if (!hasPortalSession()) {
      // `replace` so the browser Back button cannot bounce a signed-out user
      // into a shell that will only redirect them again.
      navigate('/portal/login', { replace: true });
      return;
    }
    try {
      const { data } = await customerPortalService.me();
      setProfile(data.profile);
      setBranding(data.branding);
      localStorage.setItem('portalProfile', JSON.stringify(data.profile));
    } catch {
      clearPortalSession();
      navigate('/portal/login', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    if (!profile) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await customerPortalService.notifications({ unread: 'true' });
        if (!cancelled) setUnread(data.unread_count || 0);
      } catch {
        /* the badge is decoration — never let it break the shell */
      }
    };
    load();
    const timer = setInterval(load, 120000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [profile]);

  /**
   * The portal's own theme. Kept here rather than in each page so a tenant's
   * `primary_color` reaches every surface — buttons, chips, grid headers — from
   * one place, and so the customer-facing shell can look calmer than the ERP
   * without either side having to know about the other.
   */
  const theme = useMemo(() => {
    const primary = branding?.primary_color || '#1565C0';
    return createTheme({
      palette: {
        mode: 'light',
        primary: { main: primary },
        background: { default: '#F4F7FB', paper: '#ffffff' },
        text: { primary: '#101F35', secondary: '#5A6B82' },
        divider: '#E6EBF2',
      },
      shape: { borderRadius: 12 },
      typography: {
        fontFamily: '"Inter","Segoe UI",Roboto,Arial,sans-serif',
        h4: { fontWeight: 800, letterSpacing: -0.4 },
        h5: { fontWeight: 700, letterSpacing: -0.2 },
        h6: { fontWeight: 700 },
        subtitle1: { fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 },
      },
      components: {
        MuiPaper: { defaultProps: { elevation: 0 } },
        MuiCard: { defaultProps: { elevation: 0 } },
        MuiButton: {
          styleOverrides: {
            root: { borderRadius: 10 },
            containedPrimary: {
              boxShadow: `0 6px 16px ${alpha(primary, 0.28)}`,
              '&:hover': { boxShadow: `0 8px 22px ${alpha(primary, 0.36)}` },
            },
          },
        },
        MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 8 } } },
        MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
        MuiTooltip: { styleOverrides: { tooltip: { fontSize: 12, borderRadius: 8 } } },
      },
    });
  }, [branding?.primary_color]);

  /**
   * Syncfusion renders its own DOM, outside MUI's styling, so the grids would
   * otherwise stay in stock Material grey while everything around them moved.
   * Scoped to `.portal-shell` so nothing here can leak into the ERP screens,
   * which share the same stylesheet.
   */
  const gridStyles = useMemo(() => {
    const primary = theme.palette.primary.main;
    return {
      '.portal-shell .e-grid': {
        border: '1px solid #E6EBF2',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily: '"Inter","Segoe UI",Roboto,Arial,sans-serif',
      },
      '.portal-shell .e-grid .e-gridheader': {
        borderTop: 0,
        backgroundColor: '#F7F9FC',
      },
      '.portal-shell .e-grid .e-headercell': {
        backgroundColor: '#F7F9FC',
        color: '#5A6B82',
        fontWeight: 600,
        fontSize: 12,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
      },
      '.portal-shell .e-grid .e-row:hover .e-rowcell': {
        backgroundColor: alpha(primary, 0.05),
      },
      '.portal-shell .e-grid .e-rowcell': { fontSize: 13.5, color: '#101F35' },
      '.portal-shell .e-grid .e-toolbar': {
        borderBottom: '1px solid #E6EBF2',
        backgroundColor: '#fff',
      },
      '.portal-shell .e-grid .e-pager': {
        borderTop: '1px solid #E6EBF2',
        backgroundColor: '#fff',
      },
    };
  }, [theme.palette.primary.main]);

  const visibleNav = useMemo(() => NAV_ITEMS.filter((item) => {
    if (item.capability && !profile?.capabilities?.includes(item.capability)) return false;
    if (item.feature && branding?.features && branding.features[item.feature] === false) return false;
    return true;
  }), [profile, branding]);

  const handleSignOut = async () => {
    try { await customerPortalService.logout(); } catch { /* sign out regardless */ }
    clearPortalSession();
    navigate('/portal/login', { replace: true });
  };

  const isActive = (item) => (
    item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)
  );

  const drawerContent = (
    <Box
      sx={{ width: DRAWER_WIDTH, pt: 2, display: 'flex', flexDirection: 'column', height: '100%' }}
      role="navigation"
    >
      <Typography
        variant="caption"
        sx={{ px: 3, pb: 1, color: 'text.secondary', letterSpacing: 1.2, fontWeight: 700 }}
      >
        MENU
      </Typography>
      <List sx={{ px: 1.5 }}>
        {visibleNav.map((item) => {
          const active = isActive(item);
          return (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={active}
              onClick={() => setDrawerOpen(false)}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                py: 1.1,
                color: active ? 'primary.main' : 'text.secondary',
                bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.1) : 'transparent',
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                },
                '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 14.5,
                  fontWeight: active ? 700 : 500,
                  color: active ? 'primary.main' : 'text.primary',
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ mx: 2 }} />
      <Box sx={{ m: 2, p: 2, borderRadius: 2, bgcolor: '#F7F9FC', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }} display="block">
          Need help?
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
          {branding?.support_email || '—'}
        </Typography>
        {branding?.support_phone && (
          <Typography variant="body2">{branding.support_phone}</Typography>
        )}
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={gridStyles} />
      <PortalContext.Provider value={{ profile, branding, refresh: bootstrap }}>
        <Box
          className="portal-shell"
          sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}
        >
          <AppBar
            position="fixed"
            color="inherit"
            elevation={0}
            sx={{
              zIndex: (t) => t.zIndex.drawer + 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
              // Translucent so content scrolling under the bar reads as depth
              // rather than as a hard cut.
              backgroundColor: alpha('#ffffff', 0.85),
              backdropFilter: 'blur(10px)',
            }}
          >
            <Toolbar>
              {isMobile && (
                <IconButton edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
                  <MenuIcon />
                </IconButton>
              )}
              {branding?.logo_url ? (
                <Box component="img" src={branding.logo_url} alt=""
                     sx={{ height: 32, mr: 1.5 }} />
              ) : null}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {branding?.portal_name || 'Customer Portal'}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {profile?.customer_name}
                  {profile?.customer_code ? ` · ${profile.customer_code}` : ''}
                </Typography>
              </Box>

              <Tooltip title="Notifications">
                <IconButton component={RouterLink} to="/portal/notifications">
                  <Badge badgeContent={unread} color="error">
                    <NotificationsNoneIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title={profile?.full_name || ''}>
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 34, height: 34, fontSize: 14, fontWeight: 700,
                      background: (t) => `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${alpha(t.palette.primary.dark, 0.95)} 100%)`,
                    }}
                  >
                    {(profile?.full_name || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" fontWeight={600}>{profile?.full_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {profile?.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {profile?.role_label}
                  </Typography>
                </Box>
                <Divider />
                <MenuItem component={RouterLink} to="/portal/profile"
                          onClick={() => setAnchorEl(null)}>
                  My account
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  Sign out
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>

          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? drawerOpen : true}
            onClose={() => setDrawerOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              width: isMobile ? 0 : DRAWER_WIDTH,
              flexShrink: 0,
              '& .MuiDrawer-paper': {
                width: DRAWER_WIDTH, boxSizing: 'border-box',
                borderRight: '1px solid', borderColor: 'divider',
                backgroundColor: '#fff',
              },
            }}
          >
            {!isMobile && <Toolbar />}
            {drawerContent}
          </Drawer>

          <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
            <Toolbar />
            <Container maxWidth="xl" disableGutters>
              {children}
            </Container>
          </Box>
        </Box>
      </PortalContext.Provider>
    </ThemeProvider>
  );
};

/** Shown in place of a page whose capability the signed-in role does not hold. */
export const PortalNoAccess = ({ message = 'Your portal role does not include this section.' }) => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="h6" gutterBottom>Not available</Typography>
    <Typography color="text.secondary" gutterBottom>{message}</Typography>
    <Button component={RouterLink} to="/portal" sx={{ mt: 2 }}>Back to dashboard</Button>
  </Box>
);

export default PortalLayout;
